import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactNode } from 'react';

export type EntityTypeGroupProps = {
  /**
   * The title of the group (e.g., "Organisation", "Trust", "Business")
   */
  title: string;
  /**
   * The children to render inside the group (typically EntityTypeTile components)
   */
  children: ReactNode;
  /**
   * Optional data-testid for testing
   */
  dataTestId?: string;
};

/**
 * The tile grid: one column on phones, two from `sm`, three from `lg`, with equal height tiles in a
 * row. Shared with EntityTypeSelector, which lays out the untitled Individual tile on the same grid.
 */
export const entityTypeTileGridSx: SxProps<Theme> = (theme) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
  },
  gap: theme.semantic.spacing.base,
});

/**
 * EntityTypeGroup is a container component that groups related entity type tiles
 * under a common title.
 *
 * @example
 * ```tsx
 * <EntityTypeGroup title="Business">
 *   <EntityTypeTile
 *     icon={<BriefcaseIcon />}
 *     title="Sole Trader"
 *     description="..."
 *     hint="..."
 *   />
 *   <EntityTypeTile
 *     icon={<PartnershipIcon />}
 *     title="Partnership"
 *     description="..."
 *     hint="..."
 *   />
 * </EntityTypeGroup>
 * ```
 */
const EntityTypeGroupComponent = ({
  title,
  children,
  dataTestId,
}: Readonly<EntityTypeGroupProps>) => {
  const titleId = `entity-type-group-${title.toLowerCase().replaceAll(/\s+/g, '-')}`;

  return (
    <Stack
      data-testid={dataTestId}
      gap={(theme) => theme.semantic.spacing.base}
      component="section"
      aria-labelledby={titleId}
    >
      {/* Group Title */}
      <Typography variant="h5" id={titleId}>
        {title}
      </Typography>

      {/* Tiles Grid - ensures equal height tiles in same row */}
      <Box sx={entityTypeTileGridSx}>{children}</Box>
    </Stack>
  );
};

export const EntityTypeGroup = withClarityTheme(EntityTypeGroupComponent);
